import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Tooltip,
  Switch
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import { API_BASE_URL } from '../../../../../config/api';
import '../../AssistantSettingsTheme.css';

// Mirrors ApiKeyManagement.js, but keys are scoped to a single assistant
// (the /web-keys endpoints), rather than the current user's whole account.
const WebKeyManagement = ({ assistantId, onKeysChange }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Generation Dialog State
  const [openGenerate, setOpenGenerate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [generatedKeyData, setGeneratedKeyData] = useState(null); // { web_key, name }
  const [generating, setGenerating] = useState(false);

  const fetchKeys = async () => {
    if (!assistantId) {
      setLoading(false);
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/assistants/${assistantId}/web-keys/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load web keys');

      const data = await response.json();
      setKeys(data);
      if (onKeysChange) onKeysChange(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantId]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/assistants/${assistantId}/web-keys/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newKeyName || 'Default Key'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to generate key');
      }

      setGeneratedKeyData(data);
      setOpenGenerate(false);
      setNewKeyName('');
      fetchKeys();

    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleStatus = async (key) => {
    const newStatus = !key.is_active;

    const updatedKeys = keys.map(k => k.id === key.id ? { ...k, is_active: newStatus } : k);
    setKeys(updatedKeys);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/assistants/${assistantId}/web-keys/${key.id}/status`, {
        method: 'PATCH',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');
    } catch (err) {
      setError(err.message);
      fetchKeys();
    }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this web key? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/assistants/${assistantId}/web-keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to revoke key');

      fetchKeys();
    } catch (err) {
      setError(err.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
      <Box className="assistantSettingsRoot">
      <Box className="assistantSettingsHeaderRow">
        <Typography variant="h6" className="assistantSettingsTitle">Web Keys</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenGenerate(true)}
          className="assistantPrimaryButton"
        >
          Create New Key
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Web keys only authenticate the web chat widget for this assistant. Unlike an API key,
        they cannot be used to access any other endpoint, so they're safe to embed in your website's code.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {generatedKeyData && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setGeneratedKeyData(null)}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            New Key Generated: {generatedKeyData.name}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Please copy this key now. You will not be able to see it again.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            fullWidth
            value={generatedKeyData.web_key}
            InputProps={{ readOnly: true }}
            variant="outlined"
            className="assistantMonoInput"
          />
          <Button variant="outlined" className="assistantOutlineButton" onClick={() => copyToClipboard(generatedKeyData.web_key)} startIcon={<ContentCopyIcon />}>Copy</Button>
          </Box>
        </Alert>
      )}

      <TableContainer component={Paper} elevation={0} className="assistantTablePanel">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Prefix</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">No web keys found.</TableCell>
              </TableRow>
            ) : (
              keys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell>{key.name || 'Unnamed Key'}</TableCell>
                  <TableCell>
                    <Chip
                      label={key.prefix + '...'}
                      size="small"
                      variant="outlined"
                      className="assistantMonoChip"
                    />
                  </TableCell>
                  <TableCell>{new Date(key.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Switch
                            checked={key.is_active}
                            onChange={() => handleToggleStatus(key)}
                            color="success"
                            size="small"
                        />
                        <Typography variant="caption" sx={{ ml: 1 }}>
                            {key.is_active ? 'Active' : 'Paused'}
                        </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Revoke Key">
                      <IconButton className="assistantIconButtonDanger" onClick={() => handleRevoke(key.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openGenerate}
        onClose={() => setOpenGenerate(false)}
        PaperProps={{ className: "assistantDialogPaper" }}
      >
        <DialogTitle className="assistantDialogTitle">Generate New Web Key</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Enter a friendly name for this key to identify it later.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Key Name (e.g., Website Widget)"
            fullWidth
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
          />
        </DialogContent>
        <DialogActions className="assistantDialogActions">
          <Button onClick={() => setOpenGenerate(false)} variant="outlined" className="assistantOutlineButton">
            Cancel
          </Button>
          <Button onClick={handleGenerate} variant="contained" disabled={generating} className="assistantPrimaryButton">
            {generating ? "Generating..." : "Generate Key"}
          </Button>
        </DialogActions>

      </Dialog>
    </Box>
  );
};

export default WebKeyManagement;
